import { THERAPIST } from "@/data/catalog";
import { Phone, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/I18nProvider";
import { CITIES } from "@/data/cities";
import { STYLES } from "@/data/massageStyles";

export default function Footer() {
    const { t } = useT();

    // Cities grouped by region for a scannable SEO footer.
    const groups = CITIES.reduce((acc, c) => {
        (acc[c.region] = acc[c.region] || []).push(c);
        return acc;
    }, {});

    return (
        <footer data-testid="site-footer" className="bg-[#2C2A29] text-[#FAF7F2] mt-24">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-3 gap-12">
                <div>
                    <h3 className="font-display text-3xl text-[#E07A5F] mb-3">Jérémy Tessier</h3>
                    <p className="text-sm text-[#FAF7F2]/70 leading-relaxed">{t("footer.brand_desc")}</p>
                </div>
                <div>
                    <h4 className="text-sm uppercase tracking-[0.25em] text-[#3D8C95] mb-4">{t("footer.contact")}</h4>
                    <ul className="space-y-3 text-sm">
                        <li className="flex items-start gap-3">
                            <Phone size={16} className="mt-0.5 text-[#E07A5F]" />
                            <a href={`tel:${THERAPIST.phone}`} data-testid="footer-phone" className="hover:text-[#E07A5F] transition-colors">{THERAPIST.phone}</a>
                        </li>
                        <li className="flex items-start gap-3">
                            <Mail size={16} className="mt-0.5 text-[#E07A5F]" />
                            <a href={`mailto:${THERAPIST.email}`} data-testid="footer-email" className="hover:text-[#E07A5F] transition-colors break-all">{THERAPIST.email}</a>
                        </li>
                        <li className="flex items-start gap-3">
                            <MapPin size={16} className="mt-0.5 text-[#E07A5F]" />
                            <span data-testid="footer-address">{THERAPIST.address}</span>
                        </li>
                    </ul>
                </div>
                <div>
                    <h4 className="text-sm uppercase tracking-[0.25em] text-[#3D8C95] mb-4">{t("footer.hours")}</h4>
                    <ul className="space-y-2 text-sm text-[#FAF7F2]/80">
                        <li>{t("footer.h1")}</li>
                        <li>{t("footer.h2")}</li>
                        <li>{t("footer.h3")}</li>
                    </ul>
                </div>
            </div>

            {/* SEO local footer — internal linking for Google */}
            <div className="border-t border-[#FAF7F2]/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid md:grid-cols-3 gap-10">
                    <div>
                        <h4 className="text-sm uppercase tracking-[0.25em] text-[#3D8C95] mb-4">
                            Techniques de massage
                        </h4>
                        <ul className="space-y-2 text-sm text-[#FAF7F2]/70">
                            {STYLES.map((s) => (
                                <li key={s.slug}>
                                    <Link
                                        to={`/${s.slug}`}
                                        data-testid={`footer-style-${s.slug}`}
                                        className="hover:text-[#E07A5F] transition-colors"
                                    >
                                        {s.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {Object.entries(groups).map(([region, cities]) => (
                        <div key={region}>
                            <h4 className="text-sm uppercase tracking-[0.25em] text-[#3D8C95] mb-4">
                                {region}
                            </h4>
                            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-[#FAF7F2]/70">
                                {cities.map((c) => (
                                    <li key={c.slug}>
                                        <Link
                                            to={`/${c.slug}`}
                                            data-testid={`footer-city-${c.slug}`}
                                            className="hover:text-[#E07A5F] transition-colors"
                                        >
                                            Massage {c.city}
                                            {c.mode === "future" && (
                                                <span className="ml-1 text-[10px] text-[#E07A5F]">(2027)</span>
                                            )}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>

            <div className="border-t border-[#FAF7F2]/10 py-6 text-center text-xs text-[#FAF7F2]/50">
                © {new Date().getFullYear()} Jérémy Tessier · {t("nav.brand_sub")} · {t("footer.rights")}
            </div>
        </footer>
    );
}
